import React, { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import queryString from 'query-string';
import { withFormatter } from '@concur/nui-intl-runtime';
import {
    compose,
    isLocalizationModeEnabled,
    withLogger,
    withStylesLoaded,
} from '@concur/core-ui-shell';
import { isThemeSupported } from '@concur/ui-theming';
import Spinner from '@concur/nui-widgets/Spinner';
import Shellbar from './_Shellbar';
import {
    SERVICE_DESCRIPTION_GUIDE_ID,
    TRANSLATION_TOOL_URL_IDENTIFIER,
    DEBUG_MODE,
    DEV_MODE_RESPONSE,
    SESSION_DATA_FROM_CACHE,
    SESSION_DATA_FROM_CDS,
    SESSION_DATA_NOT_AVAILABLE,
    SESSION_DATA_NOT_FETCHED,
} from '../constants';
import FallbackComponent from '../FallbackComponent/_FallbackComponent';
import InlineError from '../FallbackComponent/_InlineError';
import withNuiIntl from '../utils/withNuiIntl';
import withStylesPlaceholder from '../utils/withStylesPlaceholder';
import fetchOnScreenHelpData, { fetchOnScreenHelpDevData } from '../utils/fetchOnScreenHelpData';
import withLoggerData from '../utils/withLoggerData';
import { getThemeCookie, removeThemeCookie } from '../utils/themeUtils';
import { getCacheKey } from '../utils/sessionStorageUtils';
import AppHeaderContext from './_AppHeaderContext';

const getProfileMenuItem = ({ formatter, isLoggedIn }) => (isLoggedIn ? {
    name: formatter.formattedMessage({ id: 'CoreUI.profile.profile' }),
    id: 'profile',
    url: '#', // too many variables to try and determine the proper profile settings page here
} : undefined
);

const getHelpMenuItem = ({ formatter, menuData }) => {
    const adjustedHelpMenuItem = menuData?.helpMenuItem || {
        name: formatter.formattedMessage({ id: 'CoreUI.help' }),
        id: 'help',
        url: '/',
    };

    // due to how language bundles load, we have have gotten the menu item, but
    // not gotten the text so let's go back and update that
    adjustedHelpMenuItem.name = formatter.formattedMessage({ id: 'CoreUI.help' });

    let helpSubMenuItems = adjustedHelpMenuItem.subItems || [];

    // remove Service Description Guide if it is present
    helpSubMenuItems = helpSubMenuItems.filter(
        (item) => item.id !== SERVICE_DESCRIPTION_GUIDE_ID,
    );

    adjustedHelpMenuItem.subItems = helpSubMenuItems.map((item) => (
        {
            ...item,
            anchorProps: {
                // format requested by analytics team
                'data-analytics-json': `{"applicationName":"helplinks","elementPath":"${item?.id}","action":"click","elementType":"link"}`,
            },
        }
    ));

    return adjustedHelpMenuItem;
};

const getMinimalMenuData = ({
    formatter, isLoggedIn, logger,
}) => {
    const correlationId = uuidv4();
    if (isLoggedIn) logger.error('Menu data is missing', { correlationId });

    return {
        helpMenuItem: getHelpMenuItem({ formatter }),
        homeMenuItem: {
            name: formatter.formattedMessage({ id: 'CoreUI.home' }),
            id: 'home',
            url: isLoggedIn ? '/home.asp' : undefined,
        },
        profileMenuItem: getProfileMenuItem({ formatter, isLoggedIn }),
        // mark this so we know this is "fake" menu data
        noMenuDataFallback: isLoggedIn ? (
            <InlineError correlationId={correlationId} />
        ) : undefined,
    };
};

const isMayFourth = () => {
    // May Fourth Easter Egg
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentDay = currentDate.getDate();

    // currentMonth is 0 indexed (Jan === 0)
    return currentMonth === 4 && currentDay === 4;
};

// in lieu of passing in user permissions, we can scan the help menu items
// for the existence of the changeLocalizationMode function call in the url
const isLocalizationUser = (helpMenuItem) => helpMenuItem?.subItems?.some(
    (item) => item.url?.indexOf(TRANSLATION_TOOL_URL_IDENTIFIER) >= 0,
);

const AppHeader = (props) => {
    const {
        brandingLogoUrl,
        brandingLogoData,
        companyName,
        employeeName,
        demo,
        formatter,
        fullName,
        menuData,
        cdsURL,
        configOptions,
        logger,
        oshCacheIdentifier,
        ...otherProps
    } = props;

    const [showSpinner, setShowSpinner] = useState(false);
    const [cdsData, setCdsData] = useState();
    const [cdsStatus, setCdsStatus] = useState(SESSION_DATA_NOT_FETCHED);

    const query = queryString.parse(window.location.search, { parseBooleans: true });
    const devModeResponse = query?.[DEV_MODE_RESPONSE];
    const isDebugMode = typeof query?.[DEBUG_MODE] === 'boolean' && query?.[DEBUG_MODE];

    useEffect(() => {
        // cookie cleanup is predicated on being signed in
        if (!props?.isLoggedIn) return;

        const themeCookieValue = getThemeCookie();

        // make sure theme cookie (if present) is a valid theme for the branding id
        if (!!themeCookieValue && !isThemeSupported(props?.brandingId, themeCookieValue)) {
            // just remove the cookie and let other processes restore/fix it
            setShowSpinner(true);
            removeThemeCookie();
            setTimeout(() => {
                window?.location?.reload();
            }, 2000);
        }
    }, [props]);

    // This is a patch for nui apps that rely on Menu.cshtml urls like
    // eslint-disable-next-line
    // javascript:if (CNQR && CNQR.currentPage && (CNQR.currentPage === 'processor') && CNQR.processor && CNQR.processor.MenuLinkClicked) {CNQR.processor.MenuLinkClicked()} else {document.location.href='?RptListFilter=processor'}
    // which Error if CNQR is not defined
    // TODO: It'd be better to do these checks here:
    // https://github.concur.com/t1/travel/blob/master/Outtask%20Web%20Server/SharedMasterPages/Gateway/Menu.cshtml
    if (typeof CNQR === 'undefined') {
        window.CNQR = {};
    }

    if (menuData && !menuData.profileMenuItem) {
        menuData.profileMenuItem = getProfileMenuItem(props);
    }

    if (menuData) {
        menuData.helpMenuItem = getHelpMenuItem(props);
    }

    const adjustedProps = {
        ...otherProps,
        menuData: menuData || getMinimalMenuData(props),
    };

    const { firstName, lastName, preferredName } = employeeName || {};
    const contextValue = useMemo(() => ({
        isMayFourth: isMayFourth(),
        isLocalizationUser: isLocalizationUser(menuData?.helpMenuItem),
        isLocalizationModeEnabled: isLocalizationModeEnabled(),
        employeeDisplayName: formatter.formatEmployeeName({
            firstName,
            lastName,
            preferredName,
            fullName,
        }),
        cdsData,
        cdsStatus,
        devModeResponse,
        isDebugMode,
        configOptions,
    }), [
        props.brandingId,
        props.themeName,
        cdsData,
        cdsStatus,
        devModeResponse,
        isDebugMode,
        configOptions,
        menuData?.helpMenuItem,
        firstName,
        lastName,
        preferredName,
        fullName,
    ]);

    useEffect(() => {
        if (isDebugMode) {
            // eslint-disable-next-line no-console
            console.log(
                '~~~ UI Floorplans Debug Data ~~~\n',
                '\tprops:',
                props,
                '\n\tcontext:',
                contextValue,
            );
        }
    }, [contextValue]);

    const fallbackComp = (errorId) => (
        <FallbackComponent
            componentCssBlock={Shellbar.cssBlock}
            correlationId={errorId}
        />
    );

    const cacheKey = getCacheKey(oshCacheIdentifier);

    const foundDataInSessionStorage = () => {
        const existingSessionData = window.sessionStorage
            && JSON.parse(window.sessionStorage.getItem(cacheKey));

        if (existingSessionData) {
            setCdsData(existingSessionData);
            setCdsStatus(SESSION_DATA_FROM_CACHE);
            return true;
        }
        return false;
    };

    const readCDSResponse = (response) => {
        setCdsData(response);
        setCdsStatus(SESSION_DATA_FROM_CDS);

        if (window?.sessionStorage) {
            window.sessionStorage.setItem(
                cacheKey,
                JSON.stringify(response),
            );
        }

        const {
            correlationId,
        } = response?.meta || {};

        logger.info('Successful fetchOnScreenHelpData', {
            correlationId,
            componentName: 'UIFloorplans.OnScreenHelp.fetchOnScreenHelpData',
        });
    };

    const handleFetchError = (error) => {
        setCdsStatus(SESSION_DATA_NOT_AVAILABLE);

        logger.error(`Failed fetchOnScreenHelpData: ${error}`, {
            correlationId: error?.correlationId || '',
            componentName: 'UIFloorplans.OnScreenHelp.fetchOnScreenHelpData',
        });
    };

    useEffect(() => {
        if (foundDataInSessionStorage() || !props?.isLoggedIn || !cdsURL) {
            return;
        }

        setCdsStatus(SESSION_DATA_NOT_FETCHED);

        if (devModeResponse) {
            // dev mode means no CDS call
            // instead, we get a defined response from the CDN
            fetchOnScreenHelpDevData(devModeResponse)
                .then(
                    readCDSResponse,
                    handleFetchError,
                )
                .catch(handleFetchError);

            return;
        }

        const featureCodes = [{ code: 'castle/isCompleteUser' }, { code: 'coreui/brandinglogo-enabled' }];
        const apiContext = {
            url: cdsURL,
        };

        const graphQLQueryArgs = {
            featureCodes,
        };

        fetchOnScreenHelpData(apiContext, graphQLQueryArgs)
            .then(
                readCDSResponse,
                handleFetchError,
            )
            .catch(handleFetchError);
    }, [
        devModeResponse,
        cdsURL,
    ]);

    const overrideIsOpusUser = props?.isLoggedIn
        && cdsURL && typeof cdsData?.isCompleteUser === 'boolean';

    return (
        <AppHeaderContext.Provider value={contextValue}>
            <Shellbar
                {...adjustedProps}
                errorComp={fallbackComp}
                // check for cft flag to determine whether to send branding logo url or not,
                // if flag is false, we don't send the url to shellbar which will make
                // shellbar render the default logo. The other components rely
                // entirely on configOptions.brandingLogoEnabled because we do not need to exclude
                // the entity for those changes
                brandingLogoUrl={(cdsData?.brandingLogoEnabled || demo) ? brandingLogoUrl : undefined}
                brandingLogoData={(cdsData?.brandingLogoEnabled || demo) ? brandingLogoData : undefined}
                companyName={companyName}
                configOptions={configOptions}
                oshCacheIdentifier={oshCacheIdentifier}
                {...(overrideIsOpusUser && { isOpusUser: cdsData?.isCompleteUser })}
            />
            <Spinner
                message={formatter.formattedMessage({ id: 'CoreUI.refreshTheme' })}
                type="fullScreen"
                visible={showSpinner}
            />
        </AppHeaderContext.Provider>
    );
};

AppHeader.displayName = 'AppHeader';

AppHeader.propTypes = {
    ...Shellbar.propTypes,
};

export default compose(
    withStylesPlaceholder,
    withStylesLoaded,
    withNuiIntl,
    withFormatter,
    withLoggerData,
    withLogger,
)(AppHeader);
